import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from 'constants/apiUrls';

export interface Employee {
  uuid: string;
  name: string;
  passcode: string;
  is_active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CreateEmployeeRequest {
  businessUuid: string;
  name?: string;
  passcode: string;
}

interface CreateEmployeeResponse {
  message: string;
  data: Employee;
}

interface UpdateEmployeeRequest {
  businessUuid: string;
  employeeUuid: string;
  name?: string;
  passcode?: string;
  is_active?: boolean;
}

interface GetEmployeesResponse {
  data?: Employee[];
  results?: Employee[];
  employees?: Employee[];
}

interface UpdateEmployeeResponse {
  message: string;
  data: Employee;
}

interface DeleteEmployeeResponse {
  message: string;
}

const employeeApi = createApi({
  reducerPath: 'employeeApi',
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_SERVER_URL }),
  tagTypes: ['Employees'],
  endpoints: (builder) => ({
    getEmployees: builder.query<GetEmployeesResponse | Employee[], { businessUuid: string }>({
      query: ({ businessUuid }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: getEmployees(businessUuid),
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ['Employees'],
    }),
    createEmployee: builder.mutation<CreateEmployeeResponse, CreateEmployeeRequest>({
      query: ({ businessUuid, name, passcode }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: createEmployee(businessUuid),
          method: 'POST',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: {
            name,
            passcode,
          },
        };
      },
      invalidatesTags: ['Employees'],
    }),
    updateEmployee: builder.mutation<UpdateEmployeeResponse, UpdateEmployeeRequest>({
      query: ({ businessUuid, employeeUuid, name, passcode, is_active }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        const body: Record<string, unknown> = {};
        if (name !== undefined) body.name = name;
        if (passcode !== undefined) body.passcode = passcode;
        if (is_active !== undefined) body.is_active = is_active;

        return {
          url: updateEmployee(businessUuid, employeeUuid),
          method: 'PATCH',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body,
        };
      },
      invalidatesTags: ['Employees'],
    }),
    deleteEmployee: builder.mutation<
      DeleteEmployeeResponse,
      { businessUuid: string; employeeUuid: string }
    >({
      query: ({ businessUuid, employeeUuid }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: deleteEmployee(businessUuid, employeeUuid),
          method: 'DELETE',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      invalidatesTags: ['Employees'],
    }),
  }),
});

export const {
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
} = employeeApi;

export default employeeApi;
